import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useMediaQuery } from "react-responsive";
import axios from "axios";
import { toast } from "react-toastify";

import { getIdFromClass } from "../../../../scheduler/component/function/common";

import { ProjectCalendarContainer, ProjectDetailSection } from "../../common/styled/project";
import { CalendarContainer } from "../../../../scheduler/component/styled/scheduler";

import SelectedEvents from "./calendar/selectedEvents";
import ProjectFullCalendar from "./calendar/projectFullCalendar";
import CalendarHeader from "./calendar/calendarHeader";

const DATE_FORMAT = "YYYY-MM-DD";
const EXCLUDED_EVENT_TYPES = ["S", "H"];
const ERROR_MESSAGES = {
  DOWNLOAD_FAILED: "다운로드 실패",
  PDF_SAVED: "PDF 파일이 저장되었습니다.",
  PDF_DOWNLOAD_ERROR: "PDF 다운로드 중 오류가 발생했습니다.",
  PDF_DOWNLOAD_FAILED: "PDF 다운로드 실패",
  PARSE_ERROR: "PDF 다운로드 실패 (에러 응답 파싱 실패)",
};

const Calendar = ({ data, setEventInfo, setShowEvent, events }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectNo } = useParams();

  const containerRef = useRef();
  const calendarRef = useRef();

  const user = useSelector((state) => state?.user?.data[0]);
  const isTablet = useMediaQuery({ query: "(max-width:1050px)" });

  const [onDate, setOnDate] = useState(moment().format(DATE_FORMAT));
  const [onPrevDate, setOnPrevDate] = useState(moment().format(DATE_FORMAT));
  const [selection, setSelection] = useState();
  const [selectEvent, setSelectEvent] = useState([]);

  // 헬퍼 함수들
  const formatDate = (date) => moment(date).format(DATE_FORMAT);

  const updateDateSelection = (prevDate, newDate) => {
    document
      .querySelectorAll(`[data-date="${formatDate(prevDate)}"]`)
      .forEach((el) => el.classList.remove("selected"));
    document
      .querySelectorAll(`[data-date="${formatDate(newDate)}"]`)
      .forEach((el) => el.classList.add("selected"));
  };

  const filterEventsByDate = (targetDate, eventsList) => {
    return eventsList.filter((event) => {
      const start = formatDate(event.startDt);
      const end = formatDate(event.endDt);
      return targetDate >= start && targetDate <= end;
    });
  };

  // 초기 날짜 세팅
  useEffect(() => {
    const date = location?.state?.onDate;
    if (date) {
      setOnDate(date);
      const { onDate, ...rest } = location.state || {};
      navigate(location.pathname, { replace: true, state: { ...rest } });
    } else {
      handleDateClick(moment().format(DATE_FORMAT));
    }
  }, []);

  // 날짜 변경 시 스타일 적용
  useEffect(() => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(onDate);

    updateDateSelection(onPrevDate, onDate);
    setOnPrevDate(onDate);
  }, [onDate, onPrevDate]);

  // 날짜 클릭
  const handleDateClick = (date) => {
    const target = formatDate(date);
    setOnDate(target);

    const filtered = filterEventsByDate(target, events);
    setSelection(target);
    setSelectEvent(filtered);
  };

  // 일정 클릭
  const handleEventClick = (item, isDirect = false) => {
    const event = isDirect ? item : getIdFromClass(events, item);

    if (!event || event?.disabled) return;

    // 태블릿에서 특정 일정 타입은 제외
    if (isTablet) {
      if (EXCLUDED_EVENT_TYPES.includes(event.gbn)) return;

      navigate(`/mypage/detailEvent/${event.scheduleNo}`, {
        state: {
          path: `/mypage/project/${projectNo}`,
          search: "",
          onDate,
          tab: 1,
        },
      });
      return;
    }

    setEventInfo(event);
    setShowEvent(true);
  };

  // 헬퍼 함수: Blob을 텍스트로 변환
  const blobToText = (blob) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsText(blob);
    });

  // 헬퍼 함수: PDF 파일 다운로드 처리
  const downloadPdfFile = (response) => {
    const disposition = response.headers["content-disposition"];
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || "schedule.pdf";

    const blob = new Blob([response.data], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 헬퍼 함수: 에러 메시지 추출
  const getErrorMessage = async (error) => {
    if (!axios.isAxiosError(error)) {
      return ERROR_MESSAGES.PDF_DOWNLOAD_ERROR;
    }

    const res = error.response;

    // Blob 에러 응답 처리
    if (res?.data instanceof Blob && res.headers["content-type"]?.includes("application/json")) {
      try {
        const text = await blobToText(res.data);
        const json = JSON.parse(text);
        return json.message || json.error || ERROR_MESSAGES.PDF_DOWNLOAD_FAILED;
      } catch {
        return ERROR_MESSAGES.PARSE_ERROR;
      }
    }

    // 일반 에러 응답 처리
    if (res?.data?.message) {
      return res.data.message;
    }

    return ERROR_MESSAGES.PDF_DOWNLOAD_ERROR;
  };

  // 스케쥴표 다운로드
  const handleDownloadPdf = async () => {
    try {
      const response = await axios.post(
        "/api/schedule-pdf",
        { projectNo, mberNo: user.mberNo },
        { responseType: "blob" },
      );

      if (response.status !== 200) {
        throw new Error(ERROR_MESSAGES.DOWNLOAD_FAILED);
      }

      downloadPdfFile(response);
      toast.success(ERROR_MESSAGES.PDF_SAVED);
    } catch (error) {
      console.error(error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  return (
    <ProjectDetailSection>
      <ProjectCalendarContainer>
        <CalendarContainer className="calendar" ref={containerRef}>
          <CalendarHeader
            onDate={onDate}
            handleDateClick={handleDateClick}
            handleDownloadPdf={handleDownloadPdf}
          />
          <ProjectFullCalendar
            data={data}
            calendarRef={calendarRef}
            handleDateClick={handleDateClick}
            handleEventClick={handleEventClick}
            containerRef={containerRef}
          />
        </CalendarContainer>
        <SelectedEvents
          selectEvent={selectEvent}
          setSelectEvent={setSelectEvent}
          selection={selection}
          handleEventClick={handleEventClick}
        />
      </ProjectCalendarContainer>
    </ProjectDetailSection>
  );
};

export default Calendar;
